import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import { encode } from "base64-arraybuffer";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import moment from "moment";
import { puedeVerUtilidadTiendas } from "./usuariosUtilidadTiendas";

// Utilidad de las ventas de tiendas. El back regresa un renglon por articulo
// vendido y aqui se agrupa por venta, modelo, linea o cliente.

const AGRUPACIONES = {
  venta: {
    titulo: "Venta",
    llave: (r) => r.idVenta,
    columnas: [
      { name: "Movimiento", field: "movimiento" },
      { name: "Tienda", field: "area" },
      { name: "Fecha", field: "fecha" },
      { name: "Cliente", field: "cliente" },
      { name: "Vendedor", field: "vendedor" },
      { name: "Nota", field: "nota" },
    ],
  },
  modelo: {
    titulo: "Modelo",
    llave: (r) => r.codigo,
    columnas: [
      { name: "Codigo", field: "codigo" },
      { name: "Modelo", field: "articulo" },
      { name: "Linea", field: "linea" },
    ],
  },
  linea: {
    titulo: "Linea",
    llave: (r) => r.linea,
    columnas: [{ name: "Linea", field: "linea" }],
  },
  cliente: {
    titulo: "Cliente",
    llave: (r) => r.cliente,
    columnas: [
      { name: "Cliente", field: "cliente" },
      { name: "Notas", field: "notas", numero: true },
    ],
  },
};

const COLUMNAS_IMPORTE = [
  { name: "Piezas", field: "cantidad", numero: true },
  { name: "Costo", field: "costoTotal", numero: true, moneda: true },
  { name: "Venta", field: "precioTotal", numero: true, moneda: true },
  { name: "Utilidad", field: "utilidad", numero: true, moneda: true },
  { name: "%", field: "porcentaje", numero: true },
];

const money = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n || 0);
const numFmt = (n) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0);
const pct = (utilidad, venta) => (venta ? (utilidad / venta) * 100 : 0);

function ReporteUtilidadesTiendas() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;
  const URL_SEND_MAIL_ATT = process.env.REACT_APP_URL_SEND_MAIL_ATT;

  const hoy = moment().format("YYYY-MM-DD");
  const inicioMes = moment().startOf("month").format("YYYY-MM-DD");

  const [renglones, setRenglones] = useState([]);
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(inicioMes);
  const [selectedFechaFin, setSelectedFechaFin] = useState(hoy);
  const [agrupar, setAgrupar] = useState("venta");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedMovimiento, setSelectedMovimiento] = useState("");

  const [modalMail, setModalMail] = useState(false);
  const toggleMail = () => setModalMail(!modalMail);
  const [mailTo, setMailTo] = useState("");

  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  const autorizado = puedeVerUtilidadTiendas(user);

  useEffect(() => {
    if (!autorizado || !selectedFechaInicio || !selectedFechaFin) return;
    showLoader();
    axios
      .get(
        `${URL_DASHBOARDS}UtilidadTiendas/${selectedFechaInicio}/${selectedFechaFin}`,
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        setRenglones(response.data);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  const config = AGRUPACIONES[agrupar];
  const columnas = [...config.columnas, ...COLUMNAS_IMPORTE];

  const areas = useMemo(
    () => [...new Set(renglones.map((r) => r.area).filter(Boolean))].sort(),
    [renglones]
  );

  // Agrupado + filtros + search + orden. Lo usan la tabla, PDF, Excel y mail.
  const datosFiltrados = useMemo(() => {
    const filtrados = renglones.filter(
      (r) =>
        (!selectedArea || r.area === selectedArea) &&
        (!selectedMovimiento || r.movimiento === selectedMovimiento)
    );

    const grupos = {};
    filtrados.forEach((r) => {
      const k = config.llave(r) || "Sin dato";
      if (!grupos[k]) {
        grupos[k] = { ...r, cantidad: 0, costoTotal: 0, precioTotal: 0, notasSet: new Set() };
      }
      const g = grupos[k];
      g.cantidad += r.cantidad || 0;
      g.costoTotal += r.costoTotal || 0;
      g.precioTotal += r.precioTotal || 0;
      g.notasSet.add(r.idVenta);
    });

    let datos = Object.values(grupos).map(({ notasSet, ...g }) => {
      const utilidad = g.precioTotal - g.costoTotal;
      return {
        ...g,
        notas: notasSet.size,
        utilidad,
        porcentaje: pct(utilidad, g.precioTotal),
      };
    });

    if (search) {
      const s = search.toLowerCase();
      datos = datos.filter((d) =>
        columnas.some((c) => String(d[c.field] ?? "").toLowerCase().includes(s))
      );
    }

    if (sorting.field) {
      const col = columnas.find((c) => c.field === sorting.field);
      const dir = sorting.order === "asc" ? 1 : -1;
      datos = [...datos].sort((a, b) =>
        col && col.numero
          ? dir * ((a[sorting.field] || 0) - (b[sorting.field] || 0))
          : dir * String(a[sorting.field] ?? "").localeCompare(String(b[sorting.field] ?? ""))
      );
    } else if (agrupar === "venta") {
      datos = [...datos].sort((a, b) => (a.fecha < b.fecha ? 1 : -1));
    } else {
      datos = [...datos].sort((a, b) => b.utilidad - a.utilidad);
    }
    return datos;
  }, [renglones, agrupar, selectedArea, selectedMovimiento, search, sorting]);

  const totales = useMemo(() => {
    const t = datosFiltrados.reduce(
      (acc, d) => ({
        cantidad: acc.cantidad + d.cantidad,
        costoTotal: acc.costoTotal + d.costoTotal,
        precioTotal: acc.precioTotal + d.precioTotal,
      }),
      { cantidad: 0, costoTotal: 0, precioTotal: 0 }
    );
    const utilidad = t.precioTotal - t.costoTotal;
    return { ...t, utilidad, porcentaje: pct(utilidad, t.precioTotal) };
  }, [datosFiltrados]);

  const commentsData = datosFiltrados.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
  );

  const formatea = (c, valor) => {
    if (c.moneda) return money(valor);
    if (c.field === "porcentaje") return `${numFmt(valor)} %`;
    if (c.numero) return numFmt(valor);
    return valor;
  };

  const titulo = `Utilidad Tiendas por ${config.titulo}`;
  const subtitulo = `${selectedFechaInicio} - ${selectedFechaFin}${
    selectedArea ? ` | ${selectedArea}` : ""
  }`;

  function armaPDF() {
    const doc = new jsPDF(agrupar === "venta" ? "landscape" : "portrait");
    const ancho = doc.internal.pageSize.getWidth();
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", ancho - 45, 5, 33, 10);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", ancho - 40, 15, 25, 25);
    doc.setFontSize(14);
    doc.text(titulo, 15, 30);
    doc.text(subtitulo, 15, 35);

    const pie = columnas.map((c, i) => {
      if (i === 0) return "Totales";
      if (c.field in totales) return formatea(c, totales[c.field]);
      return "";
    });

    doc.autoTable({
      head: [columnas.map((c) => c.name)],
      body: datosFiltrados.map((d) => columnas.map((c) => formatea(c, d[c.field]))),
      foot: [pie],
      showFoot: "lastPage",
      startY: 45,
      styles: { fontSize: 7 },
    });
    return doc;
  }

  function PDFTabla() {
    const doc = armaPDF();
    window.open(doc.output("bloburl"), "_blank");
  }

  function excel() {
    const sheetData = datosFiltrados.map((d) => {
      const fila = {};
      columnas.forEach((c) => {
        fila[c.name] = c.numero ? Number((d[c.field] || 0).toFixed(2)) : d[c.field];
      });
      return fila;
    });

    var option = {};
    option.fileName = `UtilidadTiendas_${config.titulo}`;
    option.datas = [
      {
        sheetData,
        sheetName: "UtilidadTiendas",
        sheetFilter: columnas.map((c) => c.name),
        sheetHeader: columnas.map((c) => c.name),
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  function enviaMail() {
    const doc = armaPDF();
    var att = doc.output("arraybuffer");
    var base64File = encode(att);

    axios
      .post(
        URL_SEND_MAIL_ATT,
        {
          subject: titulo,
          email: mailTo,
          fileName: "UtilidadTiendas.pdf",
          att: base64File,
          text: `<table border="0" cellpadding="0" cellspacing="0" width="100%">
         <th><img width="${process.env.REACT_APP_TAMANO_LOGO_MAIL}" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}logoInstitucion.png" /></th>
         <tr align="center" style="font-size:30px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>${process.env.REACT_APP_NOMBRE_INSTITUCION}</tr>
         <tbody>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Hola,</tr>
         <tr style="font-size:20px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px;font-weight:bold"><br>Adjunto encontraras el reporte de utilidad de tiendas (${subtitulo}).</tr>
         </tbody>
         <tr style="font-size:16px;font-family:Helvetica,Arial,sans-serif;color:#333333;padding-top:10px"><br>Favor de no responder este correo, para cualquier duda favor de comunicarse al ${process.env.REACT_APP_TELEFONO_INSTITUCION}</tr>
         <tr align="right"><br>Powered by <a href="https://lynqro.com"> <img width="20%" align="center" src="${process.env.REACT_APP_LINK_SISTEMA}Logo-Lynqro-PDF.png" /> </a> </tr>
         </table>`,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then(() => {
        toggleMail();
        Swal.fire("Good job!", "Enviado con exito", "success");
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          footer: `${error.response?.data?.message || error.message}`,
        });
        console.log(error);
      });
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {autorizado ? (
        <div className="card container col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuTiendas"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" onClick={toggleMail} color="info">
                  eMail <i class="fas fa-at"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <Modal size="sm" isOpen={modalMail} toggle={toggleMail}>
            <ModalHeader toggle={toggleMail}>
              <h4>Enviar Utilidad Tiendas</h4>
            </ModalHeader>
            <ModalBody>
              <Label className="mr-sm-2">Email</Label>
              <Input
                className="col-sm-12"
                type="text"
                value={mailTo}
                required
                onChange={(e) => {
                  setMailTo(e.target.value);
                }}
              />
            </ModalBody>
            <ModalFooter>
              <Button size="sm" color="success" onClick={enviaMail}>
                Enviar
              </Button>
            </ModalFooter>
          </Modal>
          <h3 align="center">Reporte Utilidad Tiendas</h3>
          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={datosFiltrados.length}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-12 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                required
                onChange={(e) => {
                  setSelectedFechaInicio(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                required
                onChange={(e) => {
                  setSelectedFechaFin(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Agrupar por</Label>
              <Input
                bsSize="sm"
                type="select"
                value={agrupar}
                onChange={(e) => {
                  setAgrupar(e.target.value);
                  setSorting({ field: "", order: "" });
                  setCurrentPage(1);
                }}
              >
                <option value="venta">Venta</option>
                <option value="modelo">Modelo</option>
                <option value="linea">Linea</option>
                <option value="cliente">Cliente</option>
              </Input>
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Tienda</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedArea}
                onChange={(e) => {
                  setSelectedArea(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas</option>
                {areas.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Movimiento</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedMovimiento}
                onChange={(e) => {
                  setSelectedMovimiento(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Ventas y devoluciones</option>
                <option value="Venta">Solo ventas</option>
                <option value="Devolucion">Solo devoluciones</option>
              </Input>
            </Col>
          </Row>
          <br />
          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={columnas.map((c) => ({ ...c, sortable: true }))}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tbody>
              {commentsData.map((d, i) => (
                <tr key={i}>
                  {columnas.map((c) => (
                    <td
                      key={c.field}
                      style={
                        c.field === "utilidad" && d.utilidad < 0
                          ? { color: "#dc3545" }
                          : undefined
                      }
                    >
                      {formatea(c, d[c.field])}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                {columnas.map((c, i) => (
                  <td key={c.field} className="negrita">
                    {i === config.columnas.length - 1
                      ? "TOTALES"
                      : c.field in totales
                      ? formatea(c, totales[c.field])
                      : ""}
                  </td>
                ))}
              </tr>
            </tbody>
          </Table>
          <div className="col-md-8">
            <Pagination
              total={datosFiltrados.length}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}
      <br />

      {loader}
    </>
  );
}

export default ReporteUtilidadesTiendas;
